import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  buscarContatos,
  buscarConversa,
  urlAnexo,
  urlArquivoMensagem,
  type AtendimentoAcervo,
  type ContatoAcervo,
} from '../api/acervo'
import { useContextoChatwoot } from './useContextoChatwoot'
import { BarraDeAtualizacao } from './BarraDeAtualizacao'
import { useFrescor } from './useFrescor'
import './historico.css'

/**
 * Painel de histórico embutido no Chatwoot (P-37).
 *
 * Lê o acervo migrado da Zintech pelo nosso back — desconectado da plataforma
 * de origem. O atendente vê a conversa anterior do contato sem sair do Chatwoot.
 */
/**
 * @param avulso Tela do PORTAL, fora do iframe do Chatwoot.
 *
 * 🔴 **O painel é um `dashboard app`, e o Chatwoot só o monta ao lado de uma
 * CONVERSA** — em toda a v4.16.2, `DashboardApp` é referenciado por dois
 * arquivos: a caixa de conversa e a tela de configuração. Não existe ponto de
 * montagem na tela de contato.
 *
 * ⭐ Depois da migração de 05/09 isso virou um buraco de verdade: 4.217
 * contatos passaram a ter história e **nenhuma porta**, porque conversa eles
 * não têm — só terão quando a pessoa escrever de novo. *Migrar o histórico sem
 * migrar o caminho até ele é entregar meia migração.*
 *
 * Avulso, a tela não espera o `postMessage` que ninguém vai mandar: abre direto
 * na busca, ou já no contato quando vier `?telefone=`.
 */
export function HistoricoConversa({ avulso = false }: { avulso?: boolean }) {
  const { t } = useTranslation()
  const ctx = useContextoChatwoot()

  const [numero, setNumero] = useState<string>()
  const [atendimentos, setAtendimentos] = useState<AtendimentoAcervo[]>()
  const [semHistorico, setSemHistorico] = useState(false)
  const frescor = useFrescor()
  const [aberto, setAberto] = useState<number>()

  // O telefone do contexto manda; a busca manual sobrepõe quando o atendente escolhe.
  useEffect(() => {
    if (ctx.chave) setNumero(ctx.chave)
  }, [ctx.chave])

  useEffect(() => {
    if (!numero) return

    setAtendimentos(undefined)
    setSemHistorico(false)

    buscarConversa(numero)
      .then((r) => {
        setAtendimentos(r)
        setAberto(r[0]?.protocolo)
      })
      .catch(() => setSemHistorico(true))
      .finally(frescor.marcar)

    // `ctx.versao`: ver a nota em `ContextoConversa`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numero, ctx.versao])

  // Fora do iframe não há Chatwoot para responder: esperar seria esperar para
  // sempre. É a única diferença de comportamento entre os dois modos.
  if (!avulso && ctx.origem === 'aguardando') {
    return <div className="aviso">{t('historico.aguardando')}</div>
  }

  // Contato sem telefone — Telegram, Instagram e e-mail não fornecem número.
  // O acervo é indexado por telefone, então aqui só resta a busca por nome.
  if (!numero) {
    return (
      <div className="historico">
        <div className="aviso">
          {avulso ? (
            t('historico.buscaAvulsa')
          ) : (
            <>
              {t('historico.semTelefone')}
              {ctx.nome ? ` (${ctx.nome})` : ''}.
            </>
          )}
          <div className="rodape">{t('historico.procurando')}</div>
        </div>
        <Busca inicial={ctx.nome} aoEscolher={setNumero} />
      </div>
    )
  }

  if (semHistorico) {
    return (
      <div className="historico">
        <div className="aviso">
          {t('historico.semHistorico')}
          <div className="rodape">
            {t('historico.buscamosPor', {
              tipo: t(
                ctx.chaveOrigem === 'identificador'
                  ? 'historico.tipoIdentificador'
                  : 'historico.tipoTelefone',
              ),
              valor: numero,
            })}
          </div>
        </div>
        <Busca inicial={ctx.nome} aoEscolher={setNumero} />
      </div>
    )
  }

  if (!atendimentos) return <div className="aviso">{t('historico.carregando')}</div>

  const totalMensagens = atendimentos.reduce((n, a) => n + a.mensagens.length, 0)

  return (
    <div className="historico">
      <BarraDeAtualizacao em={frescor.em} />

      <header>
        <strong>{ctx.nome ?? atendimentos[0]?.nome ?? t('historico.contato')}</strong>
        <span>
          {t('historico.resumo', {
            atendimentos: atendimentos.length,
            mensagens: totalMensagens,
          })}
        </span>
      </header>

      {atendimentos.map((a) => (
        <section key={a.protocolo}>
          <button
            className={aberto === a.protocolo ? 'cabecalho ativo' : 'cabecalho'}
            onClick={() => setAberto(aberto === a.protocolo ? undefined : a.protocolo)}
          >
            <span className="data">{formatar(a.data)}</span>
            <span className="depto">{a.departamento ?? '—'}</span>
            <span className="qtd">{a.mensagens.length}</span>
          </button>

          {aberto === a.protocolo && (
            <div className="conversa">
              {a.mensagens.map((m) => (
                <div
                  key={m.id}
                  className={[
                    'balao',
                    m.tipo === 'ATENDENTE' ? 'nosso' : 'deles',
                    m.categoria === 'PRIVATE_MESSAGE' ? 'nota' : '',
                    m.categoria?.startsWith('INFO') ? 'info' : '',
                  ].join(' ')}
                >
                  {m.categoria === 'PRIVATE_MESSAGE' && <em>{t('historico.notaInterna')}</em>}
                  {m.arquivo && (
                    <Anexo protocolo={a.protocolo} mensagem={m.id} arquivo={m.arquivo} />
                  )}
                  {(m.texto || !m.arquivo) && (
                    <p>
                      {m.categoria?.startsWith('INFO')
                        ? descreverEvento(m.texto)
                        : m.texto || <i>{t('historico.semTexto', { tipo: m.tipoMensagem ?? 'mídia' })}</i>}
                    </p>
                  )}
                  <time>
                    {m.autor ? `${m.autor} · ` : ''}
                    {formatar(m.em)}
                  </time>
                </div>
              ))}

              {a.anexos.length > 0 && (
                <div className="anexos">
                  <strong>{t('historico.anexos', { count: a.anexos.length })}</strong>
                  {a.anexos.map((x) => (
                    <a
                      key={x.id}
                      href={x.disponivel ? urlAnexo(a.protocolo, x.id) : undefined}
                      className={x.disponivel ? '' : 'indisponivel'}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {x.nome}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      ))}
    </div>
  )
}

/**
 * Anexo de uma mensagem, na posição em que foi enviado.
 *
 * Imagem aparece direto; PDF, áudio e vídeo viram link — abrir tudo inline
 * pesaria a lista, e o atendente costuma querer o texto em volta, não o arquivo.
 * O arquivo vem do nosso disco, não do CDN da Zintech.
 */
function Anexo({
  protocolo,
  mensagem,
  arquivo,
}: {
  protocolo: number
  mensagem: number
  arquivo: { nome: string | null; disponivel: boolean }
}) {
  const nome = arquivo.nome ?? 'arquivo'

  if (!arquivo.disponivel) {
    return <div className="arquivo indisponivel">📎 {nome} (não baixado)</div>
  }

  const url = urlArquivoMensagem(protocolo, mensagem)
  const ext = nome.split('.').pop()?.toLowerCase() ?? ''

  if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="arquivo imagem">
        <img src={url} alt={nome} loading="lazy" />
      </a>
    )
  }

  if (['mp3', 'ogg', 'oga', 'aac', 'm4a'].includes(ext)) {
    return <audio className="arquivo" controls preload="none" src={url} />
  }

  const icone = ext === 'pdf' ? '📄' : ['mp4', 'mov'].includes(ext) ? '🎬' : '📎'

  return (
    <a href={url} target="_blank" rel="noreferrer" className="arquivo">
      {icone} {nome}
    </a>
  )
}

/** Busca manual no acervo — por nome ou por número. */
function Busca({
  inicial,
  aoEscolher,
}: {
  inicial?: string
  aoEscolher: (numero: string) => void
}) {
  const { t } = useTranslation()
  const [termo, setTermo] = useState(inicial ?? '')
  const [resultados, setResultados] = useState<ContatoAcervo[]>()
  const [buscando, setBuscando] = useState(false)
  const [falhouBusca, setFalhouBusca] = useState(false)

  async function procurar(valor = termo) {
    // Não usar `t` como nome local: ele é o tradutor neste escopo.
    const limpo = valor.trim()
    if (limpo.length < 3) return []

    setBuscando(true)
    setFalhouBusca(false)

    try {
      const r = await buscarContatos(limpo)
      setResultados(r)
      return r
    } catch {
      // Sem isto a falha virava `Uncaught (in promise)` no console e nada na
      // tela: quem opera via o painel vazio e não sabe se não achou nada ou se
      // a busca quebrou. São conclusões opostas.
      setResultados([])
      setFalhouBusca(true)
      return []
    } finally {
      setBuscando(false)
    }
  }

  // Quando o contato já tem nome, procurar sozinho: obrigar o atendente a
  // clicar em "Buscar" com o campo preenchido é pedir trabalho à toa. Achando
  // um único contato, abre direto — é o que ele faria em seguida de qualquer jeito.
  useEffect(() => {
    if (!inicial || inicial.trim().length < 3) return

    let cancelado = false
    procurar(inicial).then((r) => {
      if (!cancelado && r.length === 1 && r[0].numero) aoEscolher(r[0].numero)
    })

    return () => {
      cancelado = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inicial])

  return (
    <div className="busca">
      <div className="linha">
        <input
          value={termo}
          placeholder={t('historico.placeholderBusca')}
          onChange={(e) => setTermo(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && procurar()}
        />
        <button onClick={() => procurar()} disabled={buscando || termo.trim().length < 3}>
          {buscando ? '…' : t('comum.buscar')}
        </button>
      </div>

      {/* Falha e vazio são conclusões OPOSTAS: uma diz "não existe no acervo",
          a outra diz "não consegui olhar". Mostrar as duas do mesmo jeito ensina
          quem opera a confiar num resultado que não existe. */}
      {falhouBusca && <p className="vazio aviso--erro">{t('historico.buscaFalhou')}</p>}

      {!falhouBusca && resultados?.length === 0 && (
        <p className="vazio">{t('historico.nadaEncontrado')}</p>
      )}

      {resultados?.map((c) => (
        <button
          key={`${c.numero}-${c.nome}`}
          className="resultado"
          onClick={() => c.numero && aoEscolher(c.numero)}
        >
          <span>{c.nome ?? c.numero}</span>
          <small>
            {c.atendimentos} atendimento(s) · {formatar(c.ultimo)}
          </small>
        </button>
      ))}
    </div>
  )
}

/**
 * Eventos de sistema vêm como JSON cru no campo de texto — por exemplo
 * `{"message":"AGENT_JOIN","parameters":["Fulano"]}`. Mostrar isso ao atendente
 * é ruído; o painel traduz para uma frase e cai no texto original se o formato
 * mudar, em vez de sumir com a informação.
 */
function descreverEvento(texto: string | null) {
  if (!texto) return null

  let evento: { message?: string; parameters?: string[] }
  try {
    evento = JSON.parse(texto)
  } catch {
    return texto
  }

  const quem = evento.parameters?.filter(Boolean).join(', ')

  switch (evento.message) {
    case 'AGENT_JOIN':
      return `${quem} entrou no atendimento`
    case 'AGENT_EXIT':
      return `${quem} saiu do atendimento`
    case 'TRANSFER':
      return `transferido para ${quem}`
    case 'FINISH':
    case 'CLOSE':
      return 'atendimento encerrado'
    default:
      return quem ? `${evento.message}: ${quem}` : (evento.message ?? texto)
  }
}

function formatar(iso: string | null) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}
